(() => {
  const storageKey = "oztrail-cart-auth-v1";
  let tail = Promise.resolve();
  let checking;
  let pending = false;
  let busy = 0;
  let lastAuth;
  let memory;
  const renderers = new Map();
  const subscribers = new Set();
  let checkFailed = false;
  function notify() {
    const state = { checking: Boolean(checking), pending: Boolean(pending || readState()?.pending), failed: checkFailed };
    for (const listener of subscribers) listener(state);
  }
  const root = () => window.Shopify?.routes?.root || "/";
  const config = () => document.getElementById("cart-auth-refresh-status");
  function readState() {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey)) || memory;
    } catch {
      return memory;
    }
  }
  function saveState(value) {
    memory = value;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(value));
    } catch {}
  }
  function run(operation, authenticationCheck = false) {
    if (!authenticationCheck) busy++;
    const result = tail.then(operation).finally(() => {
      if (!authenticationCheck) busy--;
    });
    tail = result.catch(() => {});
    return result;
  }
  function announce(key, retry = false) {
    const container = config();
    if (!container) return;
    const drawer = document.getElementById("cart-drawer");
    if (drawer?.classList.contains("open")) drawer.append(container);
    else document.body.prepend(container);
    container.hidden = !key;
    container.classList.toggle("visually-hidden", !retry);
    container.querySelector("[data-cart-auth-message]").textContent = key ? container.dataset[key] : "";
    container.querySelector("button").hidden = !retry;
  }
  async function json(path, payload) {
    const response = await fetch(root() + path, {
      signal: AbortSignal.timeout(15000),
      credentials: "same-origin",
      cache: "no-store",
      ...(payload
        ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
        : {}),
    });
    if (!response.ok) throw new Error("Cart authentication refresh failed");
    return response.json();
  }
  async function validateAuthentication() {
    const contextUrl = new URL(window.location.href);
    contextUrl.searchParams.set("section_id", "cart-auth-state");
    contextUrl.searchParams.set("_cart_auth", String(Date.now()));
    const response = await fetch(contextUrl, {
      signal: AbortSignal.timeout(15000),
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!response.ok) throw new Error("Customer context unavailable");
    const context = new DOMParser()
      .parseFromString(await response.text(), "text/html")
      .querySelector("[data-cart-auth-state]")?.dataset.cartAuthState;
    if (!["guest", "member", "member-b2b"].includes(context)) throw new Error("Invalid customer context");
    const previous = readState();
    pending = Boolean(previous?.pending || (previous?.auth && previous.auth !== context));
    const needsRecalculation = pending;
    const documentChanged = Boolean(lastAuth && lastAuth !== context);
    if (!pending && !documentChanged) {
      lastAuth = context;
      saveState({ auth: context, pending: false });
      return false;
    }
    pending = true;
    saveState({ auth: previous?.auth || lastAuth || context, pending: true });
    announce("updating");
    const cart = await json("cart.js");
    cart.items = cart.items.filter((item) => item.quantity > 0);
    const sections = [...new Set(["cart-drawer", "cart-icon-bubble", ...renderers.keys()])];
    let state;
    if (needsRecalculation && cart.items.length) {
      state = await json("cart/change.js", {
        id: cart.items[0].key,
        quantity: cart.items[0].quantity,
        sections,
        sections_url: window.location.pathname,
      });
      state.items = state.items.filter((item) => item.quantity > 0);
      if (!state.sections || sections.some((id) => typeof state.sections[id] !== "string"))
        throw new Error("Cart sections unavailable");
      const contents = (value) =>
        JSON.stringify(
          value.items.map((item) => ({
            variant: item.variant_id,
            quantity: item.quantity,
            properties: item.properties,
            plan: item.selling_plan_allocation?.selling_plan?.id ?? null,
          })),
        );
      if (contents(cart) !== contents(state)) throw new Error("Cart contents changed during refresh");
    } else {
      state = { ...cart, sections: await json("?sections=" + encodeURIComponent(sections.join(","))) };
    }
    if (sections.some((id) => typeof state.sections?.[id] !== "string")) throw new Error("Cart sections unavailable");
    for (const [id, render] of renderers) await render(state.sections[id], state);
    const icon = document.getElementById("cart-icon-bubble");
    const nextIcon = new DOMParser()
      .parseFromString(state.sections["cart-icon-bubble"], "text/html")
      .querySelector(".shopify-section");
    if (icon && !nextIcon) throw new Error("Cart icon section unavailable");
    if (icon && nextIcon) icon.innerHTML = nextIcon.innerHTML;
    lastAuth = context;
    pending = false;
    saveState({ auth: context, pending: false });
    announce("updated");
    return true;
  }
  function check() {
    if (checking) return checking;
    checkFailed = false;
    checking = run(validateAuthentication, true)
      .catch((error) => {
        checkFailed = true;
        if (pending || readState()?.pending) {
          pending = true;
        }
        announce("failed", true);
        console.warn("Cart authentication refresh failed:", error.message);
        throw error;
      })
      .finally(() => {
        checking = undefined;
        notify();
      });
    notify();
    return checking;
  }
  function guardCheckout(event) {
    const target = event.target.closest?.('#checkout, [name="checkout"], a[href*="/checkout"]');
    const isCheckoutSubmit =
      event.type === "submit" && (event.submitter?.name === "checkout" || event.submitter?.id === "checkout");
    if (!(pending || readState()?.pending) || (!target && !isCheckoutSubmit)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    check().catch(() => {});
  }
  window.aoCartActions = {
    run,
    check,
    register: (id, render) => renderers.set(id, render),
    subscribe(listener) {
      subscribers.add(listener);
      listener({ checking: Boolean(checking), pending: Boolean(pending || readState()?.pending), failed: checkFailed });
      return () => subscribers.delete(listener);
    },
  };
  document.addEventListener("click", guardCheckout, true);
  document.addEventListener("submit", guardCheckout, true);
  function guardCartInteraction(event) {
    if (!busy && !checking) return;
    const control = (event.submitter || event.target).closest?.(
      '.cart-page__remove, .cart-drawer__remove, .cart-page__quantity-select, .cart-drawer__quantity-select, .cart-page__quantity-input, [data-pdp-addon-add], [data-quantity-increase], [data-quantity-decrease], [data-cross-sell-add], [data-free-gift-accept], .edit-order-reference, [name="checkout"], #checkout',
    );
    if (!control) return;
    if (event.type === "change" && control.dataset.currentQuantity) control.value = control.dataset.currentQuantity;
    event.preventDefault();
    event.stopImmediatePropagation();
  }
  document.addEventListener("click", guardCartInteraction, true);
  document.addEventListener("change", guardCartInteraction, true);
  document.addEventListener("submit", guardCartInteraction, true);
  document.addEventListener("DOMContentLoaded", () => {
    const initial = readState();
    if (!initial) {
      const signedIn = document.body.dataset.oztrailCustomerSignedIn === "true";
      const b2b = document.body.dataset.oztrailCustomerB2b === "true";
      saveState({ auth: signedIn ? (b2b ? "member-b2b" : "member") : "guest", pending: false });
    }
    const currentDocumentAuth =
      document.body.dataset.oztrailCustomerSignedIn === "true"
        ? document.body.dataset.oztrailCustomerB2b === "true"
          ? "member-b2b"
          : "member"
        : "guest";
    lastAuth = currentDocumentAuth;
    pending = Boolean(readState()?.pending || readState()?.auth !== currentDocumentAuth);
    if (pending) saveState({ ...readState(), pending: true });
    config()
      ?.querySelector("button")
      .addEventListener("click", () => check().catch(() => {}));
  });
  window.addEventListener("pageshow", () => check().catch(() => {}));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check().catch(() => {});
  });
})();
